import { html, when } from '@microsoft/fast-element';
import {
  formatDateLong,
  getPriceRange,
  getFormattedNumber,
  formatCodDate,
  getPriceValue,
  getCreditProfileValue,
} from '../../../utils/formatting';
import type { RfqDetailModal } from './rfq-detail-modal';
import { CONTRACT_TYPE, PRODUCT_TYPE } from '../../../utils';
import { CASCADE_ENUM } from '../../../services/enum.service';

export const RfqDetailModalTemplate = html<RfqDetailModal>`
  <right-side-modal
    classes="user-modal"
    :show="${(x) => x.show}"
    allowOverlayClose="true"
    @modalClosing=${(x) => x.closeModal()}
    width="${(x) => x.width}"
    header="${(x) => x.title}"
    buttons="false"
    closeModalHeader="${(x) => x.closeModal()}"
    closeModalDescription="${(x) => x.closeModal()}"
  >
    <div class="rfq-modal-content">
      <div class="rfq-modal-details">
        ${(x) => `Last Updated: ${formatCodDate(x.data.MODIFIED_AT)}`}
      </div>
      <div class="rfq-modal-row">
        <span>Status</span>
        <span>
          ${(x) => x.enumService.getEnumTranslation(CASCADE_ENUM.RFQ_STATUS, x.data.RFQ_STATUS)}
        </span>
      </div>
      <div class="rfq-modal-row">
        <span>Product Type</span>
        <span>
          ${(x) => x.enumService.getEnumTranslation(CASCADE_ENUM.PRODUCT_TYPE, x.data.PRODUCT_TYPE)}
        </span>
      </div>
      <div class="rfq-modal-row">
        <span>Technology</span>
        <span>
          ${(x) => x.enumService.getEnumTranslation(CASCADE_ENUM.TECHNOLOGY, x.data.TECHNOLOGY)}
        </span>
      </div>
      <div class="rfq-modal-row">
        <span>Contract Type</span>
        <span>
          ${(x) =>
            x.enumService.getEnumTranslation(CASCADE_ENUM.CONTRACT_TYPE, x.data.CONTRACT_TYPE)}
        </span>
      </div>
      <div class="rfq-modal-row">
        <span>ISO</span>
        <span>${(x) => x.data.ISO}</span>
      </div>
      ${when(
        (x) => x.data.CONTRACT_TYPE != CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
        html<RfqDetailModal>`
          <div class="rfq-modal-row">
            <span>Requested Volume (MWh/year)</span>
            <span>${(x) => getFormattedNumber(x.data.REQUESTED_VOLUME)}</span>
          </div>
        `,
      )}
      ${when(
        (x) => x.data.CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
        html<RfqDetailModal>`
          <div class="rfq-modal-row">
            <span>Capacity (MW)</span>
            <span>${(x) => getFormattedNumber(x.data.CAPACITY)}</span>
          </div>
        `,
      )}
      ${when(
        (x) => x.data.CONTRACT_TYPE !== CONTRACT_TYPE.REC,
        html<RfqDetailModal>`
          <div class="rfq-modal-row">
            <span>Settlement Point</span>
            <span>${(x) => x.data.SETTLEMENT_POINT}</span>
          </div>
          <div class="rfq-modal-row">
            <span>Settlement Point Index</span>
            <span>
              ${(x) =>
                x.enumService.getEnumTranslation(
                  CASCADE_ENUM.SETTLEMENT_INDEX,
                  x.data.SETTLEMENT_INDEX_STRING,
                )}
            </span>
          </div>
        `,
      )}
      ${when(
        (x) =>
          x.data.CONTRACT_TYPE != CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED &&
          x.data.PRODUCT_TYPE !== PRODUCT_TYPE.RECS,
        html<RfqDetailModal>`
          <div class="rfq-modal-row">
            <span>Price From</span>
            <span>${(x) => getPriceValue(x.data.FIXED_PRICE_LOWER_BOUND, '', '$')}</span>
          </div>
          <div class="rfq-modal-row">
            <span>Price To</span>
            <span>${(x) => getPriceValue(x.data.FIXED_PRICE_UPPER_BOUND, '', '$')}</span>
          </div>
        `,
      )}
      ${when(
        (x) =>
          x.data.CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED ||
          x.data.PRODUCT_TYPE === PRODUCT_TYPE.RECS,
        html<RfqDetailModal>`
          <div class="rfq-modal-row">
            <span>Fixed Price</span>
            <span>${(x) => getPriceValue(x.data.FIXED_PRICE, '', '$')}</span>
          </div>
        `,
      )}
      <div class="rfq-modal-row">
        <span>Tenor Start Date</span>
        <span>${(x) => formatDateLong(x.data.TENOR_START_DATE as number)}</span>
      </div>
      <div class="rfq-modal-row">
        <span>Tenor (Months)</span>
        <span>${(x) => getFormattedNumber(x.data.TENOR_MONTHS)}</span>
      </div>
      ${when(
        (x) => x.data.CONTRACT_TYPE != CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
        html<RfqDetailModal>`
          <div class="rfq-modal-row">
            <span>RFQ Reference</span>
            <span>${(x) => x.data.RFQ_REFERENCE}</span>
          </div>
          ${when(
            (x) => x.newBuilds.includes(x.data.CONTRACT_TYPE),
            html<RfqDetailModal>`
              <div class="rfq-modal-row">
                <span>Target COD</span>
                <span>${(x) => formatCodDate(x.data.TARGET_COD)}</span>
              </div>
              <div class="rfq-modal-row">
                <span>Guaranteed COD</span>
                <span>${(x) => formatCodDate(x.data.GUARANTEED_COD)}</span>
              </div>
            `,
          )}
          <div class="rfq-modal-row">
            <span>Country</span>
            <span>${(x) => x.data.COUNTRY_NAME}</span>
          </div>
          ${when(
            (x) => x.data.COUNTRY_NAME === 'United States',
            html<RfqDetailModal>`
              <div class="rfq-modal-row">
                <span>State</span>
                <span>${(x) => x.data.STATE_NAME}</span>
              </div>
              <div class="rfq-modal-row">
                <span>City</span>
                <span>${(x) => x.data.CITY_NAME}</span>
              </div>
            `,
          )}
          <div class="rfq-modal-row">
            <span>Negative Pricing Method</span>
            <span>
              ${(x) =>
                x.enumService.getEnumTranslation(
                  CASCADE_ENUM.NEGATIVE_PRICING_METHOD,
                  x.data.RFQ_NEGATIVE_PRICING_METHOD,
                )}
            </span>
          </div>
          <div class="rfq-modal-row">
            <span>Performance Guarantee Method</span>
            <span>
              ${(x) =>
                x.enumService.getEnumTranslation(
                  CASCADE_ENUM.PERFORMANCE_GUARANTEE_METHOD,
                  x.data.RFQ_PERFORMANCE_GUARANTEE_METHOD,
                )}
            </span>
          </div>
          <div class="rfq-modal-row">
            <span>Applicable REC Program</span>
            <span>
              ${(x) =>
                x.enumService.getEnumTranslation(
                  CASCADE_ENUM.RFQ_APPLICABLE_REC_PROGRAM,
                  x.data.RFQ_APPLICABLE_REC_PROGRAM,
                )}
            </span>
          </div>
          <div class="rfq-modal-row">
            <span>Applicable Tracking System</span>
            <span>
              ${(x) =>
                x.enumService.getEnumTranslation(
                  CASCADE_ENUM.APPLICABLE_TRACKING_SYSTEM,
                  x.data.APPLICABLE_TRACKING_SYSTEM,
                )}
            </span>
          </div>
          <div class="rfq-modal-row">
            <span>REC Delivery Methodology</span>
            <span>
              ${(x) =>
                x.enumService.getEnumTranslation(
                  CASCADE_ENUM.RFQ_REC_DELIVERY_METHODOLOGY,
                  x.data.RFQ_REC_DELIVERY_METHODOLOGY,
                )}
            </span>
          </div>
          <div class="rfq-modal-row">
            <span>Buyer Post-COD Credit Support (Max)</span>
            <span>
              ${(x) =>
                getCreditProfileValue(x.data.RFQ_CREDIT_PROFILE, x.data.BUYER_POST_COD_COLLATERAL)}
            </span>
          </div>
          <div class="rfq-modal-row">
            <span>Expected Settlement Range ($/MWh)</span>
            <span>
              ${(x) =>
                getPriceRange(
                  getFormattedNumber(x.data.EXPECTED_SETTLEMENT_MIN),
                  getFormattedNumber(x.data.EXPECTED_SETTLEMENT_MAX),
                )}
            </span>
          </div>
          <div class="rfq-modal-row">
            <span>Downside Risk Range ($/MWh)</span>
            <span>
              ${(x) =>
                getPriceRange(
                  getFormattedNumber(x.data.DOWNSIDE_RISK_MIN),
                  getFormattedNumber(x.data.DOWNSIDE_RISK_MAX),
                )}
            </span>
          </div>
          <div class="rfq-modal-row">
            <span>Historical Nodal LME Range (MT CO2/MWh)</span>
            <span>
              ${(x) => getFormattedNumber(x.data.NODAL_LME_HISTORIC_MIN)}
              ${(x) => (x.data.NODAL_LME_HISTORIC_MIN && x.data.NODAL_LME_HISTORIC_MAX ? '-' : '')}
              ${(x) => getFormattedNumber(x.data.NODAL_LME_HISTORIC_MAX)}
            </span>
          </div>
          <div class="rfq-modal-row">
            <span>Implied Carbon Price Range ($/MT CO2)</span>
            <span>
              ${(x) =>
                getPriceRange(
                  getFormattedNumber(x.data.IMPLIED_CARBON_PRICE_MIN),
                  getFormattedNumber(x.data.IMPLIED_CARBON_PRICE_MAX),
                )}
            </span>
          </div>
          <div class="rfq-modal-row">
            <span>Carbon Free Energy Range (%)</span>
            <span>
              ${(x) => getFormattedNumber(x.data.CARBON_FREE_ENERGY_LOWER_BOUND)}
              ${(x) =>
                x.data.CARBON_FREE_ENERGY_LOWER_BOUND && x.data.CARBON_FREE_ENERGY_UPPER_BOUND
                  ? '-'
                  : ''}
              ${(x) => getFormattedNumber(x.data.CARBON_FREE_ENERGY_UPPER_BOUND)}
            </span>
          </div>
        `,
      )}
    </div>
  </right-side-modal>
`;
